import { ReactElement, useEffect, useRef, useContext } from 'react';
import React from 'react';
import { AppAndInstanceSwitcherContext, KeyToggleContext } from '@context';

export const TEMPLATES = {
  PUB_KEY: '{{pub_key}}',
  SECRET_KEY: '{{secret}}',
  FAPI_URL: '{{fapi_url}}'
} as const;

const ZERO_WIDTH_SPACE = '\u200B';
const BOUND_KEY_REGEX = /\u200B.+?\u200B/g;
const PROD_HIDDEN = `sk_live_••••••••••••••••••••••••••••••••••`;
const DEV_HIDDEN = `sk_test_••••••••••••••••••••••••••••••••••`;

function injectKeyValue(
  node: Element,
  value: string,
  template: (typeof TEMPLATES)[keyof typeof TEMPLATES]
) {
  if (!node.textContent) return;

  // When rendering the key value, bound it with invisible characters so we can find it later for visibility toggling.
  const boundedKeyValue = `${ZERO_WIDTH_SPACE}${value}${ZERO_WIDTH_SPACE}`;

  // First, replace the initial template value with the actual bounded key value.
  node.textContent = node.textContent?.replaceAll(template, boundedKeyValue);

  // Next, attempt to replace the bounded key value with the new value.
  node.textContent = node.textContent?.replace(
    BOUND_KEY_REGEX,
    boundedKeyValue
  );
}

/**
 * Wraps a <CodeBlock /> and injects the user's current Clerk instance's publishable and secret keys.
 *
 * The nodes are detected by the `data-clerk-pk` and `data-clerk-secret` attributes.
 * @see [The remark plugin that injects the attributes](../plugins/rehype-plugin-annotate.ts)
 */
export function InjectKeys({ children }: { children: ReactElement }) {
  const ref = useRef<HTMLDivElement>(null);
  const { currentInstance } = useContext(AppAndInstanceSwitcherContext);
  const { showKeys } = useContext(KeyToggleContext);

  useEffect(() => {
    const env = currentInstance?.environment_type || 'development';
    const hiddenKeyValue = env === 'production' ? PROD_HIDDEN : DEV_HIDDEN;

    const pubKey = currentInstance?.publishable_key || 'your_publishable_key';
    const secretKey = showKeys
      ? currentInstance?.secret_key || 'your_secret_key'
      : hiddenKeyValue;
    const fapiUrl = currentInstance?.fapi_key || 'your_frontend_api_url';

    const pkNodes = ref.current?.querySelectorAll('[data-clerk-pk]');
    const secretNodes = ref.current?.querySelectorAll('[data-clerk-secret]');
    const fapiNodes = ref.current?.querySelectorAll('[data-clerk-fapi]');

    if (pkNodes) {
      for (const pkNode of pkNodes) {
        injectKeyValue(pkNode, pubKey, TEMPLATES.PUB_KEY);
      }
    }

    if (secretNodes) {
      for (const secretNode of secretNodes) {
        injectKeyValue(secretNode, secretKey, TEMPLATES.SECRET_KEY);
      }
    }

    if (fapiNodes) {
      for (const fapiNode of fapiNodes) {
        injectKeyValue(fapiNode, fapiUrl, TEMPLATES.FAPI_URL);
      }
    }
  }, [currentInstance, showKeys]);

  return <div ref={ref}>{children}</div>;
}

/**
 * A hook that exposes a function to inject the user's current Clerk instance's keys into a template string.
 */
export function useInjectKeys() {
  const { currentInstance } = useContext(AppAndInstanceSwitcherContext);

  return (source: string) => {
    const pubKey = currentInstance?.publishable_key || 'your_publishable_key';
    const secretKey = currentInstance?.secret_key || 'your_secret_key';
    const fapiUrl = currentInstance?.fapi_key || 'your_frontend_api_url';

    return source
      .replace(TEMPLATES.PUB_KEY, pubKey)
      .replace(TEMPLATES.SECRET_KEY, secretKey)
      .replace(TEMPLATES.FAPI_URL, fapiUrl);
  };
}
